import React from "react";

import { Card } from "@/components/primitives/Card";
import { FlowStats } from "@/types/stats";

interface KpiCardsProps {
  stats: FlowStats;
}

type Tone = "success" | "warning" | "danger" | "neutral";

const toneClasses: Record<Tone, string> = {
  success: "border-l-emerald-500",
  warning: "border-l-amber-500",
  danger: "border-l-red-500",
  neutral: "border-l-softnu-500",
};

export const KpiCards: React.FC<KpiCardsProps> = ({ stats }) => {
  const cards = [
    {
      label: stats.is_imported_event ? "Event success rate" : "Uptime",
      value: stats.is_imported_event && stats.success_count + stats.failure_count === 0 ? "—" : `${stats.uptime_percentage.toFixed(2)}%`,
      sub: stats.is_imported_event ? `${stats.success_count} success / ${stats.success_count + stats.failure_count} classified · ${stats.unknown_count} unknown` : `${stats.success_count} success / ${stats.total_runs} total`,
      tone:
        stats.uptime_percentage >= 99 ? "success" : stats.uptime_percentage >= 95 ? "warning" : "danger",
    },
    {
      label: stats.is_imported_event ? "Avg Execution Time" : "Avg Response Time",
      value: stats.average_response_time_ms != null ? `${stats.average_response_time_ms.toFixed(0)} ms` : "—",
      sub: "Over selected period",
      tone: "neutral" as const,
    },
    {
      label: "Failures",
      value: String(stats.failure_count),
      sub: stats.failure_count === 1 ? "1 incident" : `${stats.failure_count} incidents`,
      tone: stats.failure_count === 0 ? "success" : "danger",
    },
    {
      label: "Last Run",
      value: stats.last_run_status ?? "—",
      sub: stats.last_run_at ? new Date(stats.last_run_at).toLocaleString() : "No runs yet",
      tone:
        stats.last_run_status === "success"
          ? "success"
          : stats.last_run_status === "failure"
            ? "danger"
            : "neutral",
    },
  ];

  return (
    <div className="mb-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <Card
          key={card.label}
          className={`border-l-4 ${toneClasses[card.tone as Tone]} p-5`}
        >
          <p className="text-sm font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {card.label}
          </p>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{card.value}</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{card.sub}</p>
        </Card>
      ))}
    </div>
  );
};
